import React, { useEffect, useContext, useCallback } from "react";

// Third-party
import PropTypes from "prop-types";
import { debounce } from "debounce";

// Services
import {
  getKeyStoreKey,
  createKeyStore,
  patchKeyStoreKey
} from "services/keystore";

// Constants
import {
  LOCAL_SETTINGS_KEY_NAME,
  PROJECTS_PROFILE_VIEW_MODES,
  KPI_REFRESH_SELECTION_SETTINGS_KEY
} from "assets/global";

// Hooks
import useGoogleAnalytics, { GACategories } from "hooks/useGoogleAnalytics";

const DispatchContext = React.createContext();
const StateContext = React.createContext();

// Shared ref to prevent race conditions across all hook instances
const loadingRef = { current: false };

/**
 * Persists the whole local settings object to the backend keystore.
 * Debounced so rapid changes (e.g. toggling several checkboxes) collapse
 * into a single request.
 *
 * @param {Object} settings Local settings object to persist.
 */
const persistLocalSettingsToKeystore = debounce(settings => {
  patchKeyStoreKey(LOCAL_SETTINGS_KEY_NAME, {
    key: LOCAL_SETTINGS_KEY_NAME,
    value: settings
  }).catch(() => {
    // Expected to fail while logging out (token cleared) or offline; avoid
    // an unhandled rejection.
  });
}, 400);

/**
 * Guards the persisted KPI selection value against shape drift. Returns an
 * array of class name strings, dropping any non-string entries.
 *
 * @param {*} raw Raw value read from local storage or the keystore.
 * @returns {string[]}
 */
function normalizeKpiSelectionValue(raw) {
  if (Array.isArray(raw)) {
    return raw.filter(name => typeof name === "string");
  }
  return [];
}

const initialState = {
  localSettings: null,
  isFetching: false,
  isReady: false
};

function LocalSettingsReducer(state, action) {
  switch (action.type) {
    case "LOCAL_SETTINGS_SET": {
      return {
        ...state,
        localSettings: action.payload.localSettings
      };
    }

    case "LOCAL_SETTINGS_CLEAR": {
      return {
        ...state,
        localSettings: null
      };
    }

    case "FETCHING_START": {
      return {
        ...state,
        isFetching: true
      };
    }

    case "FETCHING_END": {
      return {
        ...state,
        isFetching: false
      };
    }

    case "SET_READY": {
      return {
        ...state,
        isReady: true
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Provides all children with the state and the dispatch of Local Settings Provider
 * @param { node } children
 * @returns node
 */
function LocalSettingsProvider({ children }) {
  const [state, dispatch] = React.useReducer(
    LocalSettingsReducer,
    initialState
  );

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
}

LocalSettingsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access and manage the state of the Local Settings Context
 */
const useLocalSettingsContext = () => {
  const localSettingsState = useContext(StateContext);
  const localSettingsDispatch = useContext(DispatchContext);
  const { trackEvent } = useGoogleAnalytics();

  // PRIVATE METHODS   //////////////////////////

  /**
   * Method that returns a copy of the localSettings currently
   * in the state. The copy has no references to the original
   * object.
   */
  const getLocalSettingsClone = useCallback(() => {
    const stored = localStorage.getItem(LOCAL_SETTINGS_KEY_NAME);
    if (stored) return JSON.parse(stored);

    return localSettingsState?.localSettings
      ? JSON.parse(JSON.stringify(localSettingsState.localSettings))
      : {};
  }, [localSettingsState?.localSettings]);

  /**
   * Method to load the local settings to the state.
   *
   * It first check if we have settings in the browser (local
   * storage) and in that case, use them. Otherwise, it checks
   * the BE for the settings.
   *
   * In the case that we had no settings either in the
   * browser or in BE, it sets an empty object as settings and
   * creates the key in the state, the browser and BE.
   */
  const loadLocalSettings = useCallback(() => {
    // Prevent multiple simultaneous calls
    if (loadingRef.current || localSettingsState.isFetching) {
      return;
    }
    loadingRef.current = true;
    localSettingsDispatch({ type: "FETCHING_START" });

    // First we try to recover settings from the browser.
    const browserSettings = localStorage.getItem(LOCAL_SETTINGS_KEY_NAME);

    if (!browserSettings) {
      // If there're no setting in the browser, we call BE
      // to try to find the settings there.
      getKeyStoreKey(LOCAL_SETTINGS_KEY_NAME)
        .then(res => {
          // If we find settings in BE, we store them into
          // the context and also in the browser.
          localStorage.setItem(
            LOCAL_SETTINGS_KEY_NAME,
            JSON.stringify(res.value)
          );

          localSettingsDispatch({
            type: "LOCAL_SETTINGS_SET",
            payload: { localSettings: res.value }
          });

          localSettingsDispatch({ type: "FETCHING_END" });
          loadingRef.current = false;
          return true;
        })
        .catch(err => {
          // If there're no setting in BE then we create the settings
          // as an empty structure and store them in the context, BE and
          // browser.

          // We should check the error here but because 404 eror is intecepted
          // and filtered returning just the text and
          // from here is not possible to know the status
          // or error code.

          // We store the empty settings in BE.
          createKeyStore(LOCAL_SETTINGS_KEY_NAME, {})
            .then(res => {
              // If success, we store the settings in the browser
              // and in local storage.
              localStorage.setItem(LOCAL_SETTINGS_KEY_NAME, JSON.stringify({}));
              localSettingsDispatch({
                type: "LOCAL_SETTINGS_SET",
                payload: { localSettings: {} }
              });

              localSettingsDispatch({ type: "FETCHING_END" });
              loadingRef.current = false;
            })
            .catch(err => {
              // If there's an error saving to BE we store the
              // setting in the browser and in the state anyway.

              // TODO: Report the error to Sentry.
              localStorage.setItem(LOCAL_SETTINGS_KEY_NAME, JSON.stringify({}));
              localSettingsDispatch({
                type: "LOCAL_SETTINGS_SET",
                payload: { localSettings: {} }
              });

              localSettingsDispatch({ type: "FETCHING_END" });
              loadingRef.current = false;
            });

          return true;
        });
    } else {
      // If there're setting in the browser we load them
      // into the context.
      localSettingsDispatch({
        type: "LOCAL_SETTINGS_SET",
        payload: { localSettings: JSON.parse(browserSettings) }
      });

      localSettingsDispatch({ type: "FETCHING_END" });
      loadingRef.current = false;

      // TODO: Check if there're setting in BE and in case
      // they are newer, update browser data.
      return true;
    }
  }, [localSettingsDispatch, localSettingsState.isFetching]);

  // PUBLIC METHODS   //////////////////////////

  /**
   * Allowed Profile > Projects view modes.
   *
   * @typedef {"grid" | "table"} ProjectsProfileViewMode
   */

  /**
   * Method that pathces the local setting in BE
   * with the current local settings in the state.
   *
   * @returns promise
   */
  const uploadLocalSettings = () => {
    // We store the empty settings in BE.
    return patchKeyStoreKey(LOCAL_SETTINGS_KEY_NAME, {
      key: LOCAL_SETTINGS_KEY_NAME,
      value: localSettingsState.localSettings
    });
  };

  /**
   * Method to toggle the Tabbed Results value
   * in the state.
   */
  const toggleTabbedResults = () => {
    let tmpLocalSettings = getLocalSettingsClone();

    tmpLocalSettings.tabbedResults = !tmpLocalSettings.tabbedResults;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });

    trackEvent({
      category: GACategories.View,
      action: "TabbedResults"
    });
  };

  /**
   * Method to change the Base Map selected
   * in the state.
   * @param {*} baseMap
   */
  const setActiveBaseMap = baseMap => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings) tmpLocalSettings = {};

    tmpLocalSettings.baseMap = baseMap;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to toggle the pylons view value
   * in the state.
   */
  const toggleShowPylons = () => {
    let tmpLocalSettings = getLocalSettingsClone();

    tmpLocalSettings.showPylons = !tmpLocalSettings.showPylons;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to toggle the labels view value in the state.
   */
  const toggleShowLabels = () => {
    let tmpLocalSettings = getLocalSettingsClone();

    tmpLocalSettings.showLabels = !tmpLocalSettings.showLabels;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to toggle the labels view value in the state.
   */
  const toggleShowMapRoutingWidth = () => {
    let tmpLocalSettings = getLocalSettingsClone();

    tmpLocalSettings.showMapRoutingWidth =
      !tmpLocalSettings.showMapRoutingWidth;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method set the last scenario selected in a project
   * in the state.
   * {project_id : scenario_id}
   */
  const setLastScenario = (project, scenario) => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings.lastScenarioByProject) {
      tmpLocalSettings.lastScenarioByProject = {};
    }

    tmpLocalSettings.lastScenarioByProject[project] = scenario;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to delete the project and its last scenario from
   * in the state.
   */
  const deleteLocalProjectAndScenario = project => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings.lastScenarioByProject) {
      tmpLocalSettings.lastScenarioByProject = {};
    }

    delete tmpLocalSettings.lastScenarioByProject[project];

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to set the visibility of an item in the local storage.
   *
   * @param {string} item Type of item: `resistanceMaps`, `corridors', 'comparisonMaps' or `paths`.
   * @param {number} id Id of the item.
   * @param {boolean} value Visibility value: true or false.
   */
  const setLocalVisibility = (item, id, value) => {
    let tmpLocalSettings = getLocalSettingsClone() || {};

    if (!tmpLocalSettings.map) tmpLocalSettings.map = {};
    if (!tmpLocalSettings.map.visibility) tmpLocalSettings.map.visibility = {};

    if (!tmpLocalSettings.map.visibility[item])
      tmpLocalSettings.map.visibility[item] = [];

    const visible = tmpLocalSettings.map.visibility[item].includes(id);

    if (value === true && !visible) {
      tmpLocalSettings.map.visibility[item].push(id);
    } else if (value === false && visible) {
      tmpLocalSettings.map.visibility[item] = tmpLocalSettings.map.visibility[
        item
      ].filter(n => n !== id);
    }

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  const getVisibilitySettings = item => {
    let tmpLocalSettings = getLocalSettingsClone() || {};

    if (!tmpLocalSettings.map) return null;
    if (!tmpLocalSettings.map.visibility) return null;
    if (!tmpLocalSettings.map.visibility[item]) return null;

    return tmpLocalSettings.map.visibility[item];
  };

  /**
   * Method to set the visibility of a set of items in the local storage.
   *
   * @param {string} item Type of item: `resistanceMaps`, `corridors', 'comparisonMaps' or `paths`.
   * @param {array} ids Array of item ids.
   * @param {boolean} value Visibility value: true or false.
   */
  const setLocalVisibilityAll = (item, ids, value) => {
    // Get visibility setting for the item (resistanceMaps, corridors, comparisonMaps or paths)
    const tmpLocalVisibilitySettings = getVisibilitySettings(item);

    // Update the visibility item array with the new ids.
    let newSettings = [];
    if (value === true) {
      // Merging the new ids if visibility is true.
      newSettings = [
        ...new Set([...(tmpLocalVisibilitySettings || []), ...ids])
      ];
    } else {
      // Removing the new ids if visibility is false.
      newSettings = tmpLocalVisibilitySettings
        ? tmpLocalVisibilitySettings.filter(item => !ids.includes(item))
        : [];
    }

    // Get all visibility local settings.
    let tmpLocalSettings = getLocalSettingsClone() || {};
    if (!tmpLocalSettings.map) tmpLocalSettings.map = {};
    if (!tmpLocalSettings.map.visibility) tmpLocalSettings.map.visibility = {};
    if (!tmpLocalSettings.map.visibility[item])
      tmpLocalSettings.map.visibility[item] = [];

    // Add the new visibility settings.
    tmpLocalSettings.map.visibility[item] = newSettings;

    // Store new local settings.
    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    // Dispatch new local settings.
    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to remove unused items in the visibility local storage.
   *
   * @param {string} item Type of item: `resistanceMaps`, `corridors', 'comparisonMaps' or `paths`.
   * @param {array} ids Array of item ids.
   */
  // const purgeLocalVisibility = (item, ids) => {
  //   // Get visibility setting for the item (resistanceMaps, corridors, comparisonMaps or paths)
  //   const tmpLocalVisibilitySettings = getVisibilitySettings(item);

  //   console.log("Visibility Settings: ", tmpLocalVisibilitySettings);
  //   console.log("ITEMS TO KEEP: ", ids);

  //   let newSettings = [];
  //   if (ids) {
  //     // Filter the ids included in the function parameter.
  //     newSettings = tmpLocalVisibilitySettings.filter(item =>
  //       ids.includes(item)
  //     );
  //   }

  //   console.log("New Settings after Purge: ", newSettings);

  //   // Get all visibility local settings.
  //   let tmpLocalSettings = getLocalSettingsClone();
  //   if (!tmpLocalSettings.map) tmpLocalSettings.map = {};
  //   if (!tmpLocalSettings.map.visibility) tmpLocalSettings.map.visibility = {};
  //   if (!tmpLocalSettings.map.visibility[item])
  //     tmpLocalSettings.map.visibility[item] = [];

  //   // Add the new visibility settings.
  //   tmpLocalSettings.map.visibility[item] = newSettings;

  //   // Store new local settings.
  //   localStorage.setItem(
  //     LOCAL_SETTINGS_KEY_NAME,
  //     JSON.stringify(tmpLocalSettings)
  //   );

  //   // Dispatch new local settings.
  //   localSettingsDispatch({
  //     type: "LOCAL_SETTINGS_SET",
  //     payload: { localSettings: tmpLocalSettings }
  //   });
  // };

  /**
   * Method to set the opacity of an item in the local storage.
   *
   * @param {string} item Type of item: `resistanceMaps`, `corridors', 'comparisonMaps' or `paths`.
   * @param {number} id Id of the item.
   * @param {boolean} value Opacity value: 0 to 1
   */
  const setLocalOpacity = (item, id, value) => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings.map) tmpLocalSettings.map = {};
    if (!tmpLocalSettings.map.opacity) tmpLocalSettings.map.opacity = {};

    if (!tmpLocalSettings.map.opacity[item])
      tmpLocalSettings.map.opacity[item] = {};

    if (tmpLocalSettings.map.opacity[item].hasOwnProperty(id)) {
      tmpLocalSettings.map.opacity[item][id] = value;
    } else {
      tmpLocalSettings.map.opacity[item] = {
        ...tmpLocalSettings.map.opacity[item],
        [id]: value
      };
    }

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to set the opacity of an item in the local storage.
   *
   * @param {string} item Type of item: `layer` or `path`.
   * @param {number} id Id of the item.
   * @param {boolean} value Opacity value: 0 to 1
   */
  const setLocalColor = (item, id, value) => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings.map) tmpLocalSettings.map = {};
    if (!tmpLocalSettings.map.color) tmpLocalSettings.map.color = {};

    if (!tmpLocalSettings.map.color[item])
      tmpLocalSettings.map.color[item] = {};

    if (tmpLocalSettings.map.color[item].hasOwnProperty(id)) {
      tmpLocalSettings.map.color[item][id] = value;
    } else {
      tmpLocalSettings.map.color[item] = {
        ...tmpLocalSettings.map.color[item],
        [id]: value
      };
    }

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  const setScenarioSelectorHeight = (project, height) => {
    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings.scenarioSelectorHeight)
      tmpLocalSettings.scenarioSelectorHeight = {};
    tmpLocalSettings.scenarioSelectorHeight[project] = height;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  const setExportScenarioResults = (
    includeItems,
    pathFormats,
    coordinateReferenceSystem
  ) => {
    let tmpLocalSettings = getLocalSettingsClone();

    tmpLocalSettings.exportScenarioResultsDialog = {
      includeItems,
      pathFormats,
      coordinateReferenceSystem
    };

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Method to set the preferred view mode in Profile > Projects.
   *
   * @param {ProjectsProfileViewMode} mode - Preferred mode.
   */
  const setProjectsProfileViewMode = mode => {
    if (
      mode !== PROJECTS_PROFILE_VIEW_MODES.GRID &&
      mode !== PROJECTS_PROFILE_VIEW_MODES.TABLE
    ) {
      return;
    }

    let tmpLocalSettings = getLocalSettingsClone();

    if (!tmpLocalSettings) {
      tmpLocalSettings = {};
    }

    tmpLocalSettings.projectsProfileViewMode = mode;

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });
  };

  /**
   * Returns the persisted KPI refresh selection (array of class names) for a
   * scenario, or `null` when no selection has ever been saved for it.
   *
   * The `null` vs `[]` distinction matters: `null` means "never saved" (so
   * the dialog can fall back to the scenario default) while `[]` means the
   * user intentionally deselected everything.
   *
   * @param {number|string} scenarioId Id of the scenario.
   * @returns {string[]|null}
   */
  const getKpiRefreshSelection = useCallback(
    scenarioId => {
      if (scenarioId === null || scenarioId === undefined) return null;

      const tmpLocalSettings = getLocalSettingsClone() || {};
      const map = tmpLocalSettings[KPI_REFRESH_SELECTION_SETTINGS_KEY];
      if (!map || typeof map !== "object") return null;

      const key = String(scenarioId);
      if (!Object.prototype.hasOwnProperty.call(map, key)) return null;

      return normalizeKpiSelectionValue(map[key]);
    },
    [getLocalSettingsClone]
  );

  /**
   * Persists the KPI refresh selection for a scenario. Writes it to local
   * storage, dispatches it to the state and debounces a keystore patch.
   *
   * @param {number|string} scenarioId Id of the scenario.
   * @param {string[]} classNames Selected KPI class names.
   */
  const setKpiRefreshSelection = (scenarioId, classNames) => {
    if (scenarioId === null || scenarioId === undefined) return;

    let tmpLocalSettings = getLocalSettingsClone() || {};

    if (
      !tmpLocalSettings[KPI_REFRESH_SELECTION_SETTINGS_KEY] ||
      typeof tmpLocalSettings[KPI_REFRESH_SELECTION_SETTINGS_KEY] !== "object"
    ) {
      tmpLocalSettings[KPI_REFRESH_SELECTION_SETTINGS_KEY] = {};
    }

    tmpLocalSettings[KPI_REFRESH_SELECTION_SETTINGS_KEY][String(scenarioId)] =
      normalizeKpiSelectionValue(classNames);

    localStorage.setItem(
      LOCAL_SETTINGS_KEY_NAME,
      JSON.stringify(tmpLocalSettings)
    );

    localSettingsDispatch({
      type: "LOCAL_SETTINGS_SET",
      payload: { localSettings: tmpLocalSettings }
    });

    persistLocalSettingsToKeystore(tmpLocalSettings);
  };

  // EFFECTS  //////////////////////////

  /**
   * LOCAL SETTINGS RETRIEVAL
   *
   * Effect that checks if localSettings state
   * is null and we are not fetching to
   * launch the loadLocalSettings method.
   */
  useEffect(() => {
    if (localSettingsState.localSettings === null) {
      loadLocalSettings();
    }
  }, [loadLocalSettings, localSettingsState.localSettings]);

  /**
   * Load Defaults
   *
   * Effect that checks if the different settings exists
   * and sets the default value in case it doesn't.
   */
  useEffect(() => {
    if (
      localSettingsState.localSettings &&
      !localSettingsState.isFetching &&
      !localSettingsState.isReady
    ) {
      let tmpLocalSettings = getLocalSettingsClone();

      // Tabbed results default.
      if (!tmpLocalSettings.hasOwnProperty("tabbedResults")) {
        tmpLocalSettings.tabbedResults = false;
      }
      // Show pylons default.
      if (!tmpLocalSettings.hasOwnProperty("showPylons")) {
        tmpLocalSettings.showPylons = true;
      }

      // Show labels default.
      if (!tmpLocalSettings.hasOwnProperty("showLabels")) {
        tmpLocalSettings.showLabels = false;
      }
      // Show Map Routing Width default.
      if (!tmpLocalSettings.hasOwnProperty("showMapRoutingWidth")) {
        tmpLocalSettings.showMapRoutingWidth = true;
      }

      // Last Scenario by project default (empty)
      if (
        !localSettingsState.localSettings.hasOwnProperty(
          "lastScenarioByProject"
        )
      ) {
        tmpLocalSettings.lastScenarioByProject = {};
      }

      // Export Scenario Results Dialog
      if (!tmpLocalSettings.hasOwnProperty("exportScenarioResultsDialog")) {
        tmpLocalSettings.exportScenarioResultsDialog = {
          includeItems: [],
          pathFormats: [],
          coordinateReferenceSystem: []
        };
      }

      // Profile > Projects preferred view mode.
      if (!tmpLocalSettings.hasOwnProperty("projectsProfileViewMode")) {
        tmpLocalSettings.projectsProfileViewMode =
          PROJECTS_PROFILE_VIEW_MODES.GRID;
      }

      localSettingsDispatch({
        type: "LOCAL_SETTINGS_SET",
        payload: { localSettings: tmpLocalSettings }
      });

      localStorage.setItem(
        LOCAL_SETTINGS_KEY_NAME,
        JSON.stringify(tmpLocalSettings)
      );
      // Mark Ready
      localSettingsDispatch({ type: "SET_READY" });
    }
  }, [
    getLocalSettingsClone,
    localSettingsDispatch,
    localSettingsState.isFetching,
    localSettingsState.isReady,
    localSettingsState.localSettings
  ]);

  return {
    localSettingsState,
    uploadLocalSettings,
    toggleTabbedResults,
    toggleShowPylons,
    toggleShowLabels,
    toggleShowMapRoutingWidth,
    setLastScenario,
    deleteLocalProjectAndScenario,
    setActiveBaseMap,
    setLocalVisibility,
    setLocalVisibilityAll,
    setExportScenarioResults,
    setProjectsProfileViewMode,
    // purgeLocalVisibility,
    setLocalOpacity,
    setLocalColor,
    setScenarioSelectorHeight,
    getKpiRefreshSelection,
    setKpiRefreshSelection
  };
};
export { useLocalSettingsContext };

export default LocalSettingsProvider;
